import React, { FC, useEffect, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';
import ReactNotification from 'react-notifications-component';

import { SEOWithMicro } from '@domains/seo';

import { useCommonData, useGlobalData } from '@stores/global-data';
import { setIsOpenCatalogMenu } from '@stores/header';
import { resetProductsList } from '@stores/products-list';
import { getUserData } from '@stores/profile';

import { APP_URL, IS_DEV } from '@utils/env';
import { isNotEmpty, isValueSet } from '@utils/guards';
import { escapeJsonLd } from '@utils/seo';

import { CookieConsent } from '@components/common/CookieConsent/CookieConsent';
import { Footer } from '@components/common/Footer/Footer';
import { Header } from '@components/common/Header/Header';
import { ScrollToTopButton } from '@components/common/ScrollToTopButton/ScrollToTopButton';
import { ConfirmDialog } from '@components/dialogs/ConfirmDialog/ConfirmDialog';
import { DevelopDialog } from '@components/dialogs/DevelopDialog/DevelopDialog';
import { FeedbackDialog } from '@components/dialogs/FeedbackDialog/FeedbackDialog';
import { FeedBackSuccessDialog } from '@components/dialogs/FeedBackSuccessDialog/FeedBackSuccessDialog';
import { OutProductsDialog } from '@components/dialogs/OutProductsDialog/OutProductsDialog';
import { LoginDialog } from '@components/login-dialog/LoginDialog/LoginDialog';
import { AddressAddDialog } from '@components/profile/AddressAddDialog/AddressAddDialog';

import styles from './DefaultLayout.module.scss';

const CommonComponents: FC = () => {
    const [isInitialized, setInitialized] = useState(false);

    useEffect(() => setInitialized(true), []);

    /* Common components shared between projects should be placed below (modals, forms, etc..) */
    return isInitialized ? (
        <>
            <ReactNotification />
            <FeedbackDialog />
            <FeedBackSuccessDialog />
            <DevelopDialog />
            <LoginDialog />
            <ConfirmDialog />
            <OutProductsDialog />
            <ScrollToTopButton />
            <AddressAddDialog />
        </>
    ) : null;
};

type DefaultLayoutProps = Partial<SEOWithMicro> & {
    siteName?: string | null;
    hasDescription?: boolean;
    isWithDefaultSuffix?: boolean;
    canonicalPath?: string | null;
};

const defaultTitle = 'Гурман-М';

export const DefaultLayout: FC<DefaultLayoutProps> = (props) => {
    const {
        children,
        title,
        siteName,
        description,
        keywords,
        image,
        isWithDefaultSuffix = false,
        hasDescription = true,
        micromarkup = null,
        canonicalPath: canonicalPathOverride,
    } = props;

    const getUserDataFn = useUnit(getUserData);
    const [resetProductsListFn, setIsOpenCatalogMenuFn] = useUnit([
        resetProductsList,
        setIsOpenCatalogMenu,
    ]);

    const {
        settings: { cookie_banner_text, yandex_metrika_counter_id, seo },
    } = useCommonData();

    const { hasSherlockHeader } = useGlobalData();

    const { asPath } = useRouter();

    const formattedDefaultSuffix = siteName ?? seo.site_name ?? defaultTitle;
    const formattedTitle =
        title === undefined || title === null
            ? formattedDefaultSuffix
            : isWithDefaultSuffix
            ? [title, formattedDefaultSuffix].join(' | ')
            : title;
    const formattedDescription = isNotEmpty(description) ? description : seo.description;

    const formattedImage = image ?? seo.image;
    const seoImage = (formattedImage?.web?.large[0]?.urls.find(
        ({ format }) => format === 'jpeg' || format === 'png',
    )?.url ?? null) as string | null;
    const seoUrl = `${APP_URL}${asPath}`;

    const path = asPath === '/' ? '' : asPath.split('?')[0];
    const fallbackCanonical = APP_URL !== null ? `${APP_URL}${path}` : null;
    const canonicalPath =
        canonicalPathOverride === undefined ? fallbackCanonical : canonicalPathOverride;
    const hasCanonicalPath = canonicalPath !== null;

    useEffect(() => {
        setIsOpenCatalogMenuFn(false);
        resetProductsListFn();
        getUserDataFn();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Head>
                <title>{formattedTitle}</title>

                {IS_DEV && !hasSherlockHeader && <meta name="robots" content="noindex, nofollow" />}

                {hasCanonicalPath && <link rel="canonical" href={canonicalPath} />}

                <meta name="og:title" content={formattedTitle} />
                <meta property="og:url" content={seoUrl} />
                <meta property="og:type" content="website" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content={seoUrl} />
                <meta name="twitter:title" content={formattedTitle} />

                {hasDescription && isValueSet(formattedDescription) && (
                    <>
                        <meta name="description" content={formattedDescription} />
                        <meta property="og:description" content={formattedDescription} />
                        <meta name="twitter:description" content={formattedDescription} />
                    </>
                )}

                {isValueSet(keywords) && <meta name="keywords" content={keywords} />}
                {isValueSet(seoImage) && (
                    <>
                        <meta property="og:image" content={seoImage} />
                        <meta property="twitter:image" content={seoImage} />
                    </>
                )}
            </Head>

            <div className={styles.root}>
                <header className={styles.header}>
                    <Header />
                </header>

                <CookieConsent text={cookie_banner_text} counterId={yandex_metrika_counter_id} />

                <main className={clsx(styles.body)}>{children}</main>

                <footer className={styles.footer} data-footer="">
                    <Footer />
                </footer>

                <CommonComponents />
            </div>

            {micromarkup !== null && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        // Поле приходит с бэкенда готовой JSON-строкой. Без замены `<`
                        // последовательность `</script>` внутри значения закроет тег,
                        // и остаток разметки браузер разберёт как HTML.
                        __html: escapeJsonLd(micromarkup),
                    }}
                />
            )}
        </>
    );
};
